import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Box from "@mui/material/Box";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { token_api } from "../../Utils/Network";

const cardSx = {
  background: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.08)",
  border: "1px solid #eef0f4",
  p: { xs: 2, md: 3 },
  mb: 3,
};

const emptyPackage = {
  name: "",
  subtitle: "",
  description: "",
  mrp: "",
  selling_price: "",
  sessions: 1,
  session_time: 30,
  booking_validity: 30,
  status: true,
};

// Rich-text toolbar for the package description (stored as HTML).
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [pkgForm, setPkgForm] = useState(emptyPackage);
  const [pkgIcon, setPkgIcon] = useState(null);
  const [pkgBanner, setPkgBanner] = useState(null);
  const [savingPkg, setSavingPkg] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");

  const fetchPackages = () => {
    token_api
      .get("counseling/v1/counselling_packages/")
      .then((res) => {
        const list = res?.data?.data?.results || res?.data?.data || res?.data?.results || res?.data || [];
        setPackages(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error("Failed to load packages:", err));
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const createPackageHandler = () => {
    if (!pkgForm.name || pkgForm.selling_price === "") {
      alert("Package Name and Selling Price are required.");
      return;
    }
    setSavingPkg(true);
    const fd = new FormData();
    fd.append("name", pkgForm.name);
    fd.append("subtitle", pkgForm.subtitle || "");
    fd.append("description", pkgForm.description || "");
    if (pkgForm.mrp !== "") fd.append("mrp", pkgForm.mrp);
    fd.append("selling_price", pkgForm.selling_price);
    fd.append("sessions", +pkgForm.sessions || 1);
    fd.append("session_time", +pkgForm.session_time || 30);
    fd.append("booking_validity", +pkgForm.booking_validity || 30);
    fd.append("status", pkgForm.status ? "true" : "false");
    if (pkgIcon) fd.append("icon", pkgIcon);
    if (pkgBanner) fd.append("banner", pkgBanner);

    // Editing an existing package -> PATCH that id; else POST a new one.
    const url = editingId
      ? `counseling/v1/counselling_packages/${editingId}/`
      : "counseling/v1/counselling_packages/";
    const method = editingId ? "patch" : "post";

    token_api[method](url, fd, {
      // Override the instance defaults (which JSON.stringify everything) so the
      // multipart FormData with icon/banner is sent raw.
      headers: { "Content-Type": "multipart/form-data" },
      transformRequest: [(d) => d],
    })
      .then(() => {
        cancelEdit();
        fetchPackages();
        alert(editingId ? "Package updated successfully!" : "Package created successfully!");
      })
      .catch((err) => {
        console.error("Package save error:", err?.response?.data || err);
        alert(err?.response?.data?.detail || "Failed to save package.");
      })
      .finally(() => setSavingPkg(false));
  };

  // Load a package into the form for editing.
  const handleEdit = (p) => {
    setEditingId(p.id);
    setPkgForm({
      name: p.name || "",
      subtitle: p.subtitle || "",
      description: p.description || "",
      mrp: p.mrp ?? "",
      selling_price: p.selling_price ?? "",
      sessions: p.sessions ?? 1,
      session_time: p.session_time ?? 30,
      booking_validity: p.booking_validity ?? 30,
      status: !!p.status,
    });
    setPkgIcon(null);
    setPkgBanner(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPkgForm(emptyPackage);
    setPkgIcon(null);
    setPkgBanner(null);
  };

  // Quick active/inactive toggle (JSON PATCH — no files involved).
  const toggleActive = (p) => {
    token_api
      .patch(`counseling/v1/counselling_packages/${p.id}/`, { status: !p.status })
      .then(() => fetchPackages())
      .catch((err) => {
        console.error("Toggle error:", err?.response?.data || err);
        alert("Failed to update status.");
      });
  };

  const filteredPackages = packages.filter((p) =>
    (p.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100%", padding: "24px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px" }}>
        <Box sx={cardSx}>
          <h2 style={{ color: "#313E6C", margin: "0 0 4px" }}>
            {editingId ? "Edit Package" : "Create Package"}
          </h2>
          <p style={{ color: "#696984", margin: "0 0 20px", fontSize: 14 }}>
            {editingId
              ? "Update this package's details and save."
              : "Create the packages your team sells. Slots (in Settings) are created per package."}
          </p>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Package Name *" size="small" fullWidth
                value={pkgForm.name}
                onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="MRP" type="number" size="small" fullWidth
                value={pkgForm.mrp}
                onChange={(e) => setPkgForm({ ...pkgForm, mrp: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Selling Price *" type="number" size="small" fullWidth
                value={pkgForm.selling_price}
                onChange={(e) => setPkgForm({ ...pkgForm, selling_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Subtitle / Tagline" size="small" fullWidth
                placeholder="e.g. Understand what's really going on"
                value={pkgForm.subtitle}
                onChange={(e) => setPkgForm({ ...pkgForm, subtitle: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} md={3}>
              <TextField
                label="Sessions" type="number" size="small" fullWidth
                value={pkgForm.sessions}
                onChange={(e) => setPkgForm({ ...pkgForm, sessions: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} md={3}>
              <TextField
                label="Session Time (min)" type="number" size="small" fullWidth
                value={pkgForm.session_time}
                onChange={(e) => setPkgForm({ ...pkgForm, session_time: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} md={3}>
              <TextField
                label="Validity (days)" type="number" size="small" fullWidth
                value={pkgForm.booking_validity}
                onChange={(e) => setPkgForm({ ...pkgForm, booking_validity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3} style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={pkgForm.status}
                style={{ color: "#12878b" }}
                onChange={(e) => setPkgForm({ ...pkgForm, status: e.target.checked })}
              />
              <span style={{ color: "#696984" }}>Active</span>
            </Grid>
            <Grid item xs={12}>
              <p style={{ color: "#313E6C", fontWeight: 600, fontSize: 13, margin: "0 0 6px" }}>
                Description
              </p>
              <style>{`.pkg-quill .ql-editor { min-height: 220px; }`}</style>
              <ReactQuill
                theme="snow"
                className="pkg-quill"
                modules={quillModules}
                value={pkgForm.description || ""}
                onChange={(html) => setPkgForm({ ...pkgForm, description: html })}
                style={{ background: "#fff", borderRadius: 8 }}
              />
              <p style={{ color: "#8a93a6", fontSize: 12, margin: "6px 0 0" }}>
                Rich text — headings, bold, lists, links, images. Saved as HTML for the detail page.
              </p>
            </Grid>
            <Grid item xs={12} md={6}>
              <span style={{ color: "#696984", marginRight: 8 }}>Icon:</span>
              <input type="file" accept="image/*" onChange={(e) => setPkgIcon(e.target.files[0] || null)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <span style={{ color: "#696984", marginRight: 8 }}>Banner:</span>
              <input type="file" accept="image/*" onChange={(e) => setPkgBanner(e.target.files[0] || null)} />
            </Grid>
            <Grid item xs={12}>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Button
                  color="primary" variant="contained" disabled={savingPkg}
                  style={{ borderRadius: "10px", backgroundColor: "#12878b", height: "2.8rem", minWidth: "12rem", fontWeight: 600 }}
                  onClick={createPackageHandler}
                >
                  {savingPkg ? "Saving..." : editingId ? "Update Package" : "Add Package"}
                </Button>
                {editingId && (
                  <Button
                    variant="outlined" disabled={savingPkg}
                    style={{ borderRadius: "10px", height: "2.8rem", minWidth: "8rem" }}
                    onClick={cancelEdit}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </Grid>
          </Grid>
        </Box>

        <Box sx={cardSx}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: 16,
            }}
          >
            <h3 style={{ color: "#313E6C", margin: 0 }}>Existing Packages</h3>
            <TextField
              size="small"
              placeholder="Search packages by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 260 }}
            />
          </div>

          {filteredPackages.length === 0 ? (
            <p style={{ color: "#696984" }}>
              {packages.length === 0 ? "No packages yet." : "No packages match your search."}
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {filteredPackages.map((p) => (
                <div
                  key={p.id}
                  style={{
                    border: "1px solid #eef0f4",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                    background: editingId === p.id ? "#eefaf6" : "#fafbfc",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontWeight: 700, color: "#313E6C", fontSize: 15 }}>
                      {p.name}
                      <span
                        style={{
                          marginLeft: 10,
                          padding: "2px 10px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          color: p.status ? "#0f6e57" : "#a3372c",
                          background: p.status ? "#d7f2e9" : "#fbe0dc",
                        }}
                      >
                        {p.status ? "Active" : "Inactive"}
                      </span>
                    </span>
                    <span style={{ color: "#696984", fontSize: 13 }}>
                      ₹{p.selling_price} · {p.sessions} session(s) · {p.session_time} min ·{" "}
                      {p.booking_validity} days validity
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ borderRadius: 8, textTransform: "none", borderColor: "#12878b", color: "#12878b" }}
                      onClick={() => handleEdit(p)}
                    >
                      Edit
                    </Button>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Switch
                        checked={!!p.status}
                        onChange={() => toggleActive(p)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#12878b" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#12878b",
                          },
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          minWidth: 52,
                          color: p.status ? "#12878b" : "#98a2b3",
                        }}
                      >
                        {p.status ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Box>
      </div>
    </div>
  );
};

export default Packages;
